import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';
import {getAffordabilityStatus, AFFORDABILITY_SCORE_EXPLANATION} from '../utils/calculations';

const {FiEdit, FiTrash2, FiCalendar, FiDollarSign, FiTrendingUp, FiCheckCircle, FiLayers, FiInfo} = FiIcons;

// Static class lookups — Tailwind's JIT compiler can't see classes built from
// template literals like `text-${color}-600`, so every combination must exist
// as a literal string somewhere in the source.
const BADGE_CLASSES = {
  green: 'text-green-600 bg-green-100',
  orange: 'text-[#f58320] bg-orange-100',
  red: 'text-red-600 bg-red-100'
};
const BAR_CLASSES = {
  green: 'bg-green-500',
  orange: 'bg-[#f58320]',
  red: 'bg-red-500'
};

function GoalCard({goal, index, onEdit, onDelete, userProfile}) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Use the metrics already computed by calculateGoalMetrics (two-phase, monthly
  // compounding) instead of recomputing here — a second, single-phase formula
  // previously lived in this file and could disagree with the numbers shown
  // elsewhere for the same goal.
  const futureValue = goal.futureValue || 0;
  const whatIfPercentage = goal.whatIfPercentage || 40;
  const whatIfFutureValue = goal.whatIfFutureValue || 0;
  const whatIfSIP = goal.whatIfSIP || 0;

  // Investment period actually used to build the SIP (may be shorter than the
  // full time horizon when the goal matures after retirement).
  const investmentHorizon = goal.investmentHorizon || goal.timeHorizon || 0;
  const totalSipInvested = (goal.requiredSIP || 0) * 12 * investmentHorizon;
  const returnsGenerated = futureValue - totalSipInvested;
  const growthPercent = totalSipInvested > 0 ? (returnsGenerated / totalSipInvested) * 100 : 0;

  // Calculate asset coverage
  const existingAssetsFutureValue = goal.existingAssetsFutureValue || 0;
  const coveragePercentage = futureValue > 0 ? (existingAssetsFutureValue / futureValue) * 100 : 0;

  const affordability = getAffordabilityStatus(goal.achievementProbability);

  const chartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        return `Year ${params[0].axisValue}: ${formatCurrency(params[0].value)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: Array.from({length: goal.timeHorizon}, (_, i) => i + 1),
      axisLabel: {fontSize: 10}
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 10,
        formatter: (value) => `₹${(value / 100000).toFixed(0)}L`
      }
    },
    series: [{
      data: goal.accumulationPath || [],
      type: 'line',
      smooth: true,
      itemStyle: {color: '#19b48d'},
      areaStyle: {color: 'rgba(25, 180, 141, 0.1)'}
    }],
    grid: {left: '10%', right: '5%', top: '10%', bottom: '15%'}
  };

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      exit={{opacity: 0, y: -20}}
      transition={{delay: index * 0.1}}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">{goal.name}</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${BADGE_CLASSES[affordability.color]}`}>
              {affordability.label}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiCalendar} className="text-xs" />
              <span>{goal.timeHorizon} years</span>
            </div>
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiDollarSign} className="text-xs" />
              <span>{formatCurrency(goal.goalAmount)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiTrendingUp} className="text-xs" />
              <span>{goal.expectedReturn}% return</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onEdit(goal)}
            className="p-2 text-gray-400 hover:text-[#19b48d] hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiEdit} className="text-sm" />
          </button>
          <button
            onClick={() => onDelete(goal.id)}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiTrash2} className="text-sm" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Metrics */}
        <div className="space-y-4">
          {/* Tagged Assets Coverage */}
          {goal.taggedAssets && goal.taggedAssets.length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center space-x-2 mb-3">
                <SafeIcon icon={FiLayers} className="text-green-600" />
                <h4 className="text-sm font-medium text-green-900">Tagged Assets Coverage</h4>
              </div>
              
              <div className="space-y-3">
                {goal.taggedAssets.map((asset, idx) => {
                  // Calculate future value of this specific asset
                  const assetFutureValue = asset.amount * Math.pow(1 + (asset.expectedReturn || goal.expectedReturn) / 100, goal.timeHorizon);
                  const assetCoveragePercent = futureValue > 0 ? (assetFutureValue / futureValue) * 100 : 0;
                  
                  return (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-green-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">
                          Asset {idx + 1}: {asset.assetKey}
                        </span>
                        <span className="text-xs font-bold text-green-700">
                          {assetCoveragePercent.toFixed(1)}% coverage
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Current Value:</span>
                          <p className="font-medium text-gray-900">{formatCurrency(asset.amount)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Future Value:</span>
                          <p className="font-medium text-green-700">{formatCurrency(assetFutureValue)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Return Rate:</span>
                          <p className="font-medium text-gray-900">{asset.expectedReturn || goal.expectedReturn}% p.a.</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Covers Goal:</span>
                          <p className="font-medium text-green-700">{formatCurrency(assetFutureValue)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Total Coverage Summary */}
                <div className="bg-green-100 rounded-lg p-3 border-t-2 border-green-400">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-green-900">Total Asset Coverage</span>
                    <span className="text-sm font-bold text-green-900">{coveragePercentage.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-white rounded-full h-3 mb-2">
                    <div 
                      className="h-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-500"
                      style={{width: `${Math.min(coveragePercentage, 100)}%`}}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-green-700">Assets Cover:</span>
                      <p className="font-bold text-green-900">{formatCurrency(existingAssetsFutureValue)}</p>
                    </div>
                    <div>
                      <span className="text-green-700">Remaining Need:</span>
                      <p className="font-bold text-orange-700">{formatCurrency(Math.max(0, futureValue - existingAssetsFutureValue))}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Future Value</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(goal.futureValue)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Required SIP</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(goal.requiredSIP)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Required Lumpsum</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(goal.requiredLumpSum)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">What-If SIP</p>
              <p className={`text-lg font-semibold text-orange-600`}>
                {formatCurrency(Math.abs(whatIfSIP || 0))}
              </p>
            </div>
          </div>

          {/* What-If Analysis */}
          <div className="bg-gradient-to-r from-[#f58320]/10 to-[#f58320]/5 rounded-lg p-4">
            <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">What-If Scenario (+{whatIfPercentage}%)</p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-700">Standard Future Value:</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(futureValue)}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-700">What-If Future Value:</span>
              <span className="text-sm font-medium text-orange-700">{formatCurrency(whatIfFutureValue)}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-700">What-If SIP Required:</span>
              <span className="text-sm font-medium text-orange-700">{formatCurrency(Math.round(whatIfSIP))}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Additional SIP Needed:</span>
              <span className="font-medium text-orange-700">{formatCurrency(Math.round(whatIfSIP - goal.requiredSIP))}</span>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#19b48d]/10 to-[#19b48d]/5 rounded-lg p-4">
            <div className="flex items-center space-x-1.5 mb-1">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Affordability Score</p>
              <span className="group relative">
                <SafeIcon icon={FiInfo} className="text-gray-400 text-xs cursor-help" />
                <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 rounded-lg bg-gray-900 text-white text-[11px] leading-snug p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  {AFFORDABILITY_SCORE_EXPLANATION}
                </span>
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${BAR_CLASSES[affordability.color]}`}
                  style={{width: `${affordability.value}%`}}
                />
              </div>
              <span className="text-sm font-semibold text-gray-900">{affordability.value}%</span>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-3">Accumulation Path</p>
          <div className="h-40">
            <ReactECharts option={chartOption} style={{height: '100%', width: '100%'}} />
          </div>

          {/* Investment Comparison */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">Total SIP Invested</p>
                <p className="text-sm font-medium text-gray-900">{formatCurrency(totalSipInvested)}</p>
                <p className="text-xs text-gray-500">Over {investmentHorizon} year{investmentHorizon === 1 ? '' : 's'} of investing</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">Returns Generated</p>
                <p className="text-sm font-medium text-green-600">
                  {formatCurrency(returnsGenerated)}
                </p>
                <p className="text-xs text-gray-500">
                  {totalSipInvested > 0 ? `${Math.round(growthPercent)}% growth` : 'Fully funded by tagged assets'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default GoalCard;